import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  ArrowLeft, 
  Users, 
  ShoppingBag, 
  DollarSign, 
  Calendar,
  Eye,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false); // Changed to false - use real data

  // Mock pharmacy accounts data
  const mockAccounts = [
    {
      id: "1",
      pharmacy_name: "Calgary Central Pharmacy",
      primary_contact_name: "Sarah Johnson",
      email: "sarah@calgarycentralrx.com",
      phone: "403-555-0101",
      address: "123 Main Street SW, Calgary, AB T2P 1A1",
      license_number: "AB12345",
      status: "approved",
      current_period_total: 3250.75,
      credit_limit: 5000.00,
      pad_agreement_signed: true,
      bank_account_holder: "Calgary Central Pharmacy Inc.",
      bank_institution_number: "001",
      bank_transit_number: "12345",
      bank_account_number: "***7890",
      created_at: "2025-10-15T08:30:00Z",
      practice_type: "Community Pharmacy"
    },
    {
      id: "2",
      pharmacy_name: "Healthwise Pharmacy",
      primary_contact_name: "Michael Chen",
      email: "mchen@healthwiserx.com",
      phone: "403-555-0202",
      address: "456 17th Avenue SE, Calgary, AB T2G 2V4",
      license_number: "AB23456",
      status: "approved",
      current_period_total: 4850.50,
      credit_limit: 5000.00,
      pad_agreement_signed: true,
      bank_account_holder: "Healthwise Pharmacy Ltd",
      bank_institution_number: "002",
      bank_transit_number: "23456",
      bank_account_number: "***4321",
      created_at: "2025-10-18T14:20:00Z",
      practice_type: "Community Pharmacy"
    },
    {
      id: "3",
      pharmacy_name: "Foothills Medical Pharmacy",
      primary_contact_name: "Dr. Emily Rodriguez",
      email: "erodriguez@foothillsmedpharm.com",
      phone: "403-555-0303",
      address: "1403 29th Street NW, Calgary, AB T2N 2T9",
      license_number: "AB34567",
      status: "approved",
      current_period_total: 1250.00,
      credit_limit: 5000.00,
      pad_agreement_signed: true,
      bank_account_holder: "Foothills Medical Pharmacy",
      bank_institution_number: "003",
      bank_transit_number: "34567",
      bank_account_number: "***9876",
      created_at: "2025-10-20T10:15:00Z",
      practice_type: "Hospital Pharmacy"
    },
    {
      id: "4",
      pharmacy_name: "QuickCare Pharmacy",
      primary_contact_name: "James Patterson",
      email: "jpatterson@quickcarepharm.com",
      phone: "403-555-0404",
      address: "789 Macleod Trail S, Calgary, AB T2H 0L3",
      license_number: "AB45678",
      status: "approved",
      current_period_total: 875.25,
      credit_limit: 5000.00,
      pad_agreement_signed: true,
      bank_account_holder: "QuickCare Pharmacy",
      bank_institution_number: "001",
      bank_transit_number: "45678",
      bank_account_number: "***5555",
      created_at: "2025-10-22T16:45:00Z",
      practice_type: "Community Pharmacy"
    },
    {
      id: "5",
      pharmacy_name: "Wellness Plus Pharmacy",
      primary_contact_name: "Lisa Thompson",
      email: "lthompson@wellnessplus.com",
      phone: "403-555-0505",
      address: "321 Centre Street N, Calgary, AB T2E 2P5",
      license_number: "AB56789",
      status: "approved",
      current_period_total: 150.00,
      credit_limit: 5000.00,
      pad_agreement_signed: false,
      bank_account_holder: "",
      bank_institution_number: "",
      bank_transit_number: "",
      bank_account_number: "",
      created_at: "2025-10-28T09:00:00Z",
      practice_type: "Community Pharmacy"
    }
  ];

  // Mock orders data
  const mockOrders = [
    {
      id: "ORD-001",
      pharmacy_name: "Calgary Central Pharmacy",
      pharmacy_id: "1",
      items: [
        { product_name: "TCV-13 Vials (13 dram)", quantity: 5, unit_price: 45.50 },
        { product_name: "CR-20 Child-Resistant Vials (20 dram)", quantity: 3, unit_price: 52.00 },
        { product_name: "RCV-30 Reversible Cap Vials (30 dram)", quantity: 2, unit_price: 48.75 }
      ],
      total_amount: 489.00,
      status: "pending",
      created_at: "2025-10-29T10:30:00Z",
      billing_period: "Oct 15 - Oct 29, 2025"
    },
    {
      id: "ORD-002",
      pharmacy_name: "Healthwise Pharmacy",
      pharmacy_id: "2",
      items: [
        { product_name: "TCV-8 Vials (8 dram)", quantity: 10, unit_price: 38.00 },
        { product_name: "OB-4 Oval Bottles (4 oz)", quantity: 4, unit_price: 28.50 }
      ],
      total_amount: 494.00,
      status: "invoiced",
      created_at: "2025-10-27T14:20:00Z",
      billing_period: "Oct 18 - Nov 1, 2025",
      invoice_number: "INV-2025-001",
      invoice_date: "2025-11-01T00:00:00Z"
    },
    {
      id: "ORD-003",
      pharmacy_name: "Foothills Medical Pharmacy",
      pharmacy_id: "3",
      items: [
        { product_name: "SC-20 Snap Cap Vials (20 dram)", quantity: 8, unit_price: 42.00 }
      ],
      total_amount: 336.00,
      status: "paid",
      created_at: "2025-10-25T11:15:00Z",
      billing_period: "Oct 20 - Nov 3, 2025",
      invoice_number: "INV-2025-002",
      invoice_date: "2025-11-03T00:00:00Z",
      paid_date: "2025-11-03T12:00:00Z"
    }
  ];

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      if (useMockData) {
        // Use mock data for demonstration
        setTimeout(() => {
          setAccounts(mockAccounts);
          setOrders(mockOrders);
          setLoading(false);
        }, 500);
      } else {
        // Fetch all user accounts from backend
        const token = localStorage.getItem('auth_token');
        const headers = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const usersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/admin/users`, {
          headers: headers
        });
        
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          setAccounts(usersData);
        } else {
          console.error('Failed to load users:', usersResponse.status);
        }

        // Fetch all orders
        const ordersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/admin/all-orders`, {
          headers: headers
        });
        
        if (ordersResponse.ok) {
          const ordersData = await ordersResponse.json();
          setOrders(ordersData.orders || []);
        }
        
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const calculateNextBillingDate = (createdAt) => {
    if (!createdAt) return 'N/A';
    const created = new Date(createdAt);
    const nextBilling = new Date(created);
    nextBilling.setDate(nextBilling.getDate() + 14);
    return formatDate(nextBilling.toISOString());
  };

  // Calculate dashboard stats
  const totalRevenue = orders
    .filter(order => order.status === 'paid')
    .reduce((sum, order) => sum + order.total_amount, 0);

  const activeOrdersCount = orders.filter(order => 
    order.status === 'pending' || order.status === 'invoiced'
  ).length;

  const invoicesDueCount = orders.filter(order => 
    order.status === 'invoiced'
  ).length;

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'invoiced': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Store
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Admin Dashboard</h1>
                <p className="text-sm text-slate-600">Alberta Pharmacy Supplies</p>
              </div>
            </div>
            
            <div 
              className="px-4 py-2 rounded-lg font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #72429C, #EF842E)' }}
            >
              Admin Panel
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-slate-900">{accounts.length}</div>
                <Users className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Active Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-slate-900">{activeOrdersCount}</div>
                <ShoppingBag className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-slate-900">${totalRevenue.toFixed(2)}</div>
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Invoices Due</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-slate-900">{invoicesDueCount}</div>
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Different Views */}
        <Tabs defaultValue="accounts" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="accounts">Pharmacy Accounts</TabsTrigger>
            <TabsTrigger value="orders">Orders & Invoices</TabsTrigger>
            <TabsTrigger value="billing">Billing Cycles</TabsTrigger>
          </TabsList>

          {/* ACCOUNTS TAB */}
          <TabsContent value="accounts">
            <Card>
              <CardHeader>
                <CardTitle>Pharmacy Accounts</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-slate-600">Loading accounts...</p>
                  </div>
                ) : accounts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-600">No pharmacy accounts registered yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {accounts.map((account) => (
                      <Card key={account.id} className="border-l-4 border-purple-600">
                        <CardContent className="pt-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left Column - Business Info */}
                            <div>
                              <h3 className="text-lg font-bold text-slate-900 mb-1">
                                {account.pharmacy_name}
                              </h3>
                              <Badge 
                                className={
                                  account.status === 'approved' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }
                              >
                                {account.status}
                              </Badge>
                              
                              <div className="mt-4 space-y-2">
                                <div className="flex items-start">
                                  <Users className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                                  <span className="text-sm text-slate-700">{account.primary_contact_name}</span>
                                </div>
                                <div className="flex items-start">
                                  <Mail className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                                  <span className="text-sm text-slate-700">{account.email}</span>
                                </div>
                                <div className="flex items-start">
                                  <Phone className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                                  <span className="text-sm text-slate-700">{account.phone}</span>
                                </div>
                                <div className="flex items-start">
                                  <MapPin className="w-4 h-4 text-slate-500 mr-2 mt-1" />
                                  <span className="text-sm text-slate-700">{account.address || 'N/A'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Right Column - Financial & Billing */}
                            <div>
                              <div className="space-y-3">
                                <div className="p-3 bg-slate-50 rounded-lg">
                                  <p className="text-xs text-slate-600 mb-1">Current Period Total</p>
                                  <p className="text-2xl font-bold text-slate-900">
                                    ${account.current_period_total?.toFixed(2) || '0.00'}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    of ${account.credit_limit?.toFixed(2) || '5000.00'} limit
                                  </p>
                                </div>

                                <div className="p-3 bg-blue-50 rounded-lg">
                                  <div className="flex items-center mb-1">
                                    <Calendar className="w-4 h-4 text-blue-600 mr-2" />
                                    <p className="text-xs text-slate-600">Next Billing Date</p>
                                  </div>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {calculateNextBillingDate(account.created_at)}
                                  </p>
                                </div>

                                <div className="p-3 bg-green-50 rounded-lg">
                                  <div className="flex items-center mb-1">
                                    <CreditCard className="w-4 h-4 text-green-600 mr-2" />
                                    <p className="text-xs text-slate-600">PAD Agreement</p>
                                  </div>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {account.pad_agreement_signed ? '✓ Signed' : '✗ Not Signed'}
                                  </p>
                                </div>

                                <div className="text-xs text-slate-500">
                                  <p>Registered: {formatDate(account.created_at)}</p>
                                  <p>License: {account.license_number || 'N/A'}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ORDERS TAB */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>Orders & Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-slate-600">Loading orders...</p>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 mb-2">No orders yet</p>
                    <p className="text-sm text-slate-500">Orders will appear here once pharmacies start ordering</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <Card key={order.id} className="border-l-4 border-orange-600">
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-bold text-slate-900">{order.pharmacy_name}</h3>
                              <p className="text-sm text-slate-600">Order #{order.id}</p>
                              <p className="text-xs text-slate-500">Placed: {formatDate(order.created_at)}</p>
                            </div>
                            <Badge className={getStatusBadgeColor(order.status)}>
                              {order.status.toUpperCase()}
                            </Badge>
                          </div>

                          <div className="bg-slate-50 rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-sm text-slate-700 mb-2">Order Items:</h4>
                            <div className="space-y-2">
                              {order.items.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span className="text-slate-700">
                                    {item.product_name} × {item.quantity}
                                  </span>
                                  <span className="font-medium text-slate-900">
                                    ${(item.unit_price * item.quantity).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-slate-600">Total Amount</p>
                              <p className="text-lg font-bold text-slate-900">${(order.total_amount || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600">Billing Period</p>
                              <p className="text-sm font-medium text-slate-700">{order.billing_period}</p>
                            </div>
                            {order.invoice_number && (
                              <div>
                                <p className="text-xs text-slate-600">Invoice Number</p>
                                <p className="text-sm font-medium text-slate-700">{order.invoice_number}</p>
                              </div>
                            )}
                            {order.paid_date && (
                              <div>
                                <p className="text-xs text-slate-600">Paid Date</p>
                                <p className="text-sm font-medium text-green-700">{formatDate(order.paid_date)}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BILLING TAB */}
          <TabsContent value="billing">
            <Card>
              <CardHeader>
                <CardTitle>Billing Cycles (2-Week Periods)</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-slate-600">Loading billing cycles...</p>
                  </div>
                ) : accounts.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 mb-2">No active billing cycles</p>
                    <p className="text-sm text-slate-500">Billing information will appear when accounts start ordering</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {accounts.map((account) => {
                      const currentTotal = account.current_period_total || 0;
                      const creditLimit = account.credit_limit || 2500;
                      const percentageUsed = (currentTotal / creditLimit) * 100;
                      const daysUntilBilling = Math.ceil(
                        (new Date(calculateNextBillingDate(account.created_at)) - new Date()) / (1000 * 60 * 60 * 24)
                      );

                      return (
                        <Card key={account.id} className="border-l-4 border-blue-600">
                          <CardContent className="pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Pharmacy Info */}
                              <div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1">{account.pharmacy_name}</h3>
                                <p className="text-sm text-slate-600">{account.primary_contact_name}</p>
                                <p className="text-xs text-slate-500 mt-2">
                                  Cycle Start: {formatDate(account.created_at)}
                                </p>
                              </div>

                              {/* Current Period Usage */}
                              <div>
                                <div className="mb-2">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-slate-600">Period Total</span>
                                    <span className="text-xs font-medium text-slate-700">
                                      {percentageUsed.toFixed(1)}% used
                                    </span>
                                  </div>
                                  <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        percentageUsed > 90 ? 'bg-red-600' :
                                        percentageUsed > 70 ? 'bg-yellow-600' :
                                        'bg-green-600'
                                      }`}
                                      style={{ width: `${Math.min(percentageUsed, 100)}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <p className="text-2xl font-bold text-slate-900">
                                  ${currentTotal.toFixed(2)}
                                </p>
                                <p className="text-xs text-slate-500">
                                  of ${creditLimit.toFixed(2)} limit
                                </p>
                                {percentageUsed > 90 && (
                                  <div className="mt-2 flex items-center text-xs text-red-600">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    Near credit limit
                                  </div>
                                )}
                              </div>

                              {/* Next Billing */}
                              <div>
                                <div className="p-4 bg-blue-50 rounded-lg">
                                  <div className="flex items-center mb-2">
                                    <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                                    <p className="text-sm font-semibold text-blue-900">Next Invoice</p>
                                  </div>
                                  <p className="text-lg font-bold text-slate-900">
                                    {calculateNextBillingDate(account.created_at)}
                                  </p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {daysUntilBilling} days remaining
                                  </p>
                                  {daysUntilBilling <= 3 && (
                                    <Badge className="mt-2 bg-orange-100 text-orange-800">
                                      Due Soon
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
